import React, { useEffect, useState } from "react";
import { AlertTriangle, Send } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { useDialogs } from "../context/DialogContext";
import adminApi from "../adminApi";
import "./MailDelivery.css";

// Stan wysyłki maili z backendu (Backend/src/services/mailStatus.js)
export interface MailStatus {
  transport: "graph" | "smtp" | "outbox" | null;
  from: string;
  testRecipient: string | null;
  alertRecipient: string | null;
  level: "ok" | "warning" | "error";
  problems: { level: "warning" | "error"; message: string }[];
  graph: {
    credentialType: "secret" | "certificate";
    configured: boolean;
    expiresAt: string | null;
    expirySource: string | null;
    daysLeft: number | null;
  } | null;
}

export const fetchMailStatus = async (): Promise<MailStatus> => (await adminApi.get("/mail/status")).data;

const TRANSPORT_LABELS: Record<string, string> = {
  graph: "Microsoft Graph (sendMail)",
  smtp: "SMTP (old account)",
  outbox: "Outbox - saved as file, not sent",
};

// Pasek nad treścią panelu, gdy z wysyłką jest problem (np. dostęp do Graph wkrótce wygasa)
export const MailStatusBanner: React.FC<{ onOpen?: () => void }> = ({ onOpen }) => {
  const { t } = useLanguage();
  const [status, setStatus] = useState<MailStatus | null>(null);

  useEffect(() => {
    fetchMailStatus().then(setStatus).catch(() => setStatus(null));
  }, []);

  if (!status || status.level === "ok") return null;
  return (
    <div className={`admin-alert admin-alert--${status.level === "error" ? "error" : "warning"} mail-banner`} role="status">
      <AlertTriangle size={16} />
      <span>
        <strong>{t("Mail delivery")}:</strong> {status.problems.map((problem) => problem.message).join(" · ")}
      </span>
      {onOpen && (
        <button type="button" className="admin-btn admin-btn--ghost" onClick={onOpen}>
          {t("Details")}
        </button>
      )}
    </div>
  );
};

function MailDelivery() {
  const { t, tf } = useLanguage();
  const { confirm, notify } = useDialogs();
  const [status, setStatus] = useState<MailStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    fetchMailStatus()
      .then(setStatus)
      .catch(() => setError(t("Failed to load mail status")));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (error) return <div className="admin-alert admin-alert--error">{error}</div>;
  if (!status) return <div className="admin-empty">{t("Loading...")}</div>;

  const testTarget = status.testRecipient || status.from;
  const canSendTest = status.transport === "graph" || status.transport === "outbox";

  const sendTestMail = async () => {
    const ok = await confirm({
      message: tf("Send a test email to {to}?", { to: testTarget }),
      confirmLabel: t("Send test email"),
    });
    if (!ok) return;
    setSending(true);
    try {
      const { data } = await adminApi.post("/mail/test");
      notify(tf("Test email sent to {to}.", { to: data.to }), "success");
    } catch (err: any) {
      notify(`${t("Test email failed")}: ${err?.response?.data?.error || err.message}`, "error");
    } finally {
      setSending(false);
    }
  };

  const graph = status.graph;
  return (
    <>
      <div className="admin-page__header">
        <div>
          <h2>{t("Mail delivery")}</h2>
          <p>{t("Emails with orders and inquiries from the configurator.")}</p>
        </div>
      </div>

      {status.problems.map((problem) => (
        <div key={problem.message} className={`admin-alert admin-alert--${problem.level === "error" ? "error" : "warning"}`}>
          {problem.message}
        </div>
      ))}

      <div className="admin-card mail-card">
        <dl className="mail-facts">
          <dt>{t("Transport")}</dt>
          <dd>{status.transport ? t(TRANSPORT_LABELS[status.transport]) : "—"}</dd>

          <dt>{t("Sender")}</dt>
          <dd>{status.from}</dd>

          <dt>{t("Test recipient")}</dt>
          <dd>
            {status.testRecipient || "—"}
            {status.testRecipient && <small>{t("Test mode: emails to customers go to this address, internal emails go to their recipient with [TEST] in the subject.")}</small>}
          </dd>

          <dt>{t("Reminders to")}</dt>
          <dd>{status.alertRecipient || "—"}</dd>

          {graph && (
            <>
              <dt>{t("Sign-in")}</dt>
              <dd>
                {graph.credentialType === "certificate" ? t("Certificate") : t("Client secret")}
                {" · "}
                {graph.configured ? t("configured") : t("not configured")}
              </dd>

              <dt>{t("Valid until")}</dt>
              <dd>
                {graph.expiresAt
                  ? `${new Date(graph.expiresAt).toLocaleDateString()} (${tf("{days} days left", { days: graph.daysLeft ?? 0 })})`
                  : "—"}
              </dd>
            </>
          )}
        </dl>

        <div className="mail-test">
          <button type="button" className="admin-btn" onClick={sendTestMail} disabled={!canSendTest || sending}>
            <Send size={16} /> {sending ? t("Sending...") : t("Send test email")}
          </button>
          <span className="mail-test__hint">
            {canSendTest
              ? tf("The test email goes to {to}.", { to: testTarget })
              : t("Test emails are only available with Microsoft Graph.")}
          </span>
        </div>
      </div>
    </>
  );
}

export default MailDelivery;
